import {
  dateValid,
  dateValidMax,
  dateValidMin,
  validateEmail,
  validatePhone,
  validateSsn,
} from "utils/validation";
import { SpeciesEnum } from "../../../../__generated__/globalTypes";
import { Step } from "./types";
import { isAfter, isBefore, isSameDay, subMonths } from "date-fns";
import { MAX_AGE_CAT, MAX_AGE_DOG } from "components-shared/insuranceData";
import { getEarliestStartDate, getLastAllowedStartDate } from "utils/insurance";
import { HealthHistoryEnum } from "../store/state/types";
import {
  getCompetitorRenewalDate,
  isCancellationAllowedByCompetitor,
} from "../components/utils/insurely/insurelyUtil";
import { selectCustomer } from "../store/selectors/customer";
import { selectCurrentInsuranceByAnimalId } from "../store/selectors/insurance";
import { selectHasDrafts } from "../store/selectors/animals";

export const stepsSwe: Step[] = [
  {
    index: 0,
    name: "new-or-switch",
    isValid: () => true,
    // getCurrentInsurance(state).previousInsurance !== undefined,
    next: ({ animal, ...state }, override) => {
      const { previousInsurance } = selectCurrentInsuranceByAnimalId(
        state,
        animal?.id
      );

      return override
        ? override
        : previousInsurance
        ? "fetch-current-insurance-question"
        : "species";
    },
  },
  {
    index: 1,
    name: "fetch-current-insurance-question",
    isValid: () => true,
    next: ({ animal, ...state }, override) => {
      const insurance = selectCurrentInsuranceByAnimalId(state, animal?.id);

      return override
        ? override
        : insurance.insurely?.shouldCompare
        ? // This doesn't make sense?
          "fetch-current-insurance-question"
        : "species";
    },
  },
  {
    index: 2,
    name: "fetch-current-insurance",
    isValid: () => true,
    next: () => "choose-animal",
    previous: () => "fetch-current-insurance-question",
  },
  {
    index: 3,
    name: "choose-animal",
    isValid: ({ animal, ...state }) => {
      const insurance = selectCurrentInsuranceByAnimalId(state, animal?.id);

      return (
        !insurance.insurely?.shouldCompare ||
        insurance.insurely?.currentInsurelyPolicyIndex !== undefined
      );
    },
    next: ({ animal, ...state }) => {
      const insurance = selectCurrentInsuranceByAnimalId(state, animal?.id);

      return insurance.insurely?.shouldCompare ? "name" : "species";
    },
  },
  {
    index: 4,
    name: "species",
    isValid: ({ animal }) => Boolean(animal?.species),
    next: () => "name",
  },
  {
    index: 5,
    name: "name",
    isValid: ({ animal }) => Boolean(animal?.name),
    next: () => "breed",
  },
  {
    index: 6,
    name: "breed",
    isValid: ({ animal }) => Boolean(animal?.breed),
    next: ({ animal }) => {
      return animal?.breed?.id === "DogBreed-blandras"
        ? "mixed-breed"
        : "animal-sex";
    },
  },
  {
    index: 7,
    name: "mixed-breed",
    next: () => "animal-sex",
    isValid: ({ animal }) => {
      if (!animal) return false;
      const { breed, mixedBreedFHalf, mixedBreedSHalf } = animal;

      return breed?.id === "DogBreed-blandras"
        ? !!mixedBreedFHalf && !!mixedBreedSHalf
        : !!breed;
    },
  },
  {
    index: 8,
    name: "animal-sex",
    next: () => "castration",
    isValid: ({ animal }) => Boolean(animal?.sex),
  },
  {
    index: 9,
    name: "castration",
    next: ({ animal }) =>
      animal?.species === SpeciesEnum.cat ? "animal-access" : "animal-birthday",
    isValid: ({ animal }) => Boolean(animal?.sexSituation),
  },
  {
    index: 10,
    name: "animal-access",
    next: () => "animal-birthday",
    isValid: ({ animal }) => {
      if (!animal) return false;

      const { species, access } = animal;

      return species === SpeciesEnum.cat ? !!access : true;
    },
  },
  {
    index: 11,
    name: "animal-birthday",
    next: ({ animal, ...state }) => {
      const { previousInsurance } = selectCurrentInsuranceByAnimalId(
        state,
        animal?.id
      );

      return previousInsurance ? "health-questions" : "address";
    },
    isValid: ({ animal }) => {
      if (!animal) return false;

      const { birthday, species } = animal;

      if (!birthday) {
        return false;
      }

      return (
        dateValid(birthday) &&
        dateValidMin(
          new Date(birthday),
          subMonths(
            new Date(),
            species === SpeciesEnum.dog ? MAX_AGE_DOG : MAX_AGE_CAT
          )
        ) &&
        dateValidMax(new Date(birthday), new Date())
      );
    },
  },
  {
    index: 12,
    name: "health-questions",
    next: ({ animal }) => {
      if (
        animal?.healthHistory.length === 1 &&
        animal?.healthHistory[0] === HealthHistoryEnum.NO_ILLNESS
      ) {
        return "address";
      }
      return "health-overview";
    },
    isValid: ({ animal }) => {
      return (
        !!animal?.healthHistory.length &&
        animal.healthHistory.some(
          (id) =>
            id === HealthHistoryEnum.NO_ILLNESS ||
            id === HealthHistoryEnum.CHRONIC_DISEASE ||
            id === HealthHistoryEnum.ILLNESS_LAST_24_MONTHS ||
            id === HealthHistoryEnum.TREATED_LAST_24_MONTHS
        )
      );
    },
  },
  {
    index: 13,
    name: "health-overview",
    next: () => "address",
    isValid: () => true,
  },
  {
    index: 14,
    name: "address",
    next: ({ animals }) => {
      if (
        animals.some(
          (animal) =>
            animal.insurances[0].insurely?.policies &&
            animal.insurances[0].insurely.shouldCompare
        )
      ) {
        return "summary-flytthjalp";
      }

      return "summary";
    },
    isValid: (state) => Boolean(selectCustomer(state).region),
  },
  {
    index: 14,
    name: "summary",
    next: () => "loading",
    isValid: () => true,
  },
  {
    index: 14,
    name: "summary-flytthjalp",
    next: () => "loading",
    isValid: () => true,
  },
  {
    index: 15,
    name: "loading",
    next: () => "quote-preview",
    isValid: ({ animal, ...state }) => selectHasDrafts(state),
  },
  {
    index: 16,
    name: "quote-preview",
    isValid: ({ animal, ...state }) => {
      if (!animal) return false;

      const { startDate, limit, deductible } = selectCurrentInsuranceByAnimalId(
        state,
        animal.id
      );
      const { birthday } = animal;

      const { eurobonusMemberIdValid, lunarMemberIdValid } =
        selectCustomer(state);

      if (!startDate || !birthday) {
        return false;
      }

      const startDateValid =
        dateValid(startDate) &&
        (isSameDay(new Date(startDate), getEarliestStartDate(birthday)) ||
          (isAfter(new Date(startDate), getEarliestStartDate(birthday)) &&
            isBefore(new Date(startDate), getLastAllowedStartDate())));

      return (
        !!eurobonusMemberIdValid &&
        !!lunarMemberIdValid &&
        !!limit &&
        (!!deductible || deductible === 0) &&
        startDateValid
      );
    },
    next: () => "add-ons",
    previous: ({ animals, metadata }) => {
      if (metadata.isResumed) {
        if (
          animals.some(
            (animal) =>
              animal.insurances[0].insurely?.policies &&
              animal.insurances[0].insurely.shouldCompare
          )
        ) {
          return "summary-flytthjalp";
        }
        return "summary";
      }
      return null;
    },
  },
  {
    index: 17,
    name: "add-ons",
    next: ({ animal, ...state }) => {
      const { insurely, startDate } = selectCurrentInsuranceByAnimalId(
        state,
        animal?.id
      );

      if (insurely?.shouldCompare) {
        const renewalDate = getCompetitorRenewalDate(insurely);
        const isCancellationPossible =
          isCancellationAllowedByCompetitor(insurely);

        return renewalDate !== startDate || !isCancellationPossible
          ? "user-info"
          : "cancel-previous-insurance";
      }

      return "user-info";
    },
    isValid: () => true,
  },
  {
    index: 17,
    name: "user-info",
    isValid: (state) => {
      const { ssn, phone, email } = selectCustomer(state);

      if (!ssn) {
        return false;
      }

      return (
        validateSsn(ssn) &&
        validateEmail(email) &&
        (phone ? validatePhone(phone) : true)
      );
    },
    next: ({ animals }) => {
      if (
        animals.some(
          (animal) =>
            animal.insurances[0].insurely?.policies &&
            animal.insurances[0].insurely.shouldCompare
        )
      ) {
        return "draft-summary-flytthjalp";
      }
      return "draft-summary";
    },
  },
  {
    index: 18,
    name: "cancel-previous-insurance",
    next: ({ animal, ...state }, override) => {
      const { insurely } = selectCurrentInsuranceByAnimalId(state, animal?.id);

      return override // this should always be in play, set from component
        ? override
        : insurely?.cancelCompetitor
        ? "power-of-attorney"
        : "user-info";
    },
    isValid: () => true,
  },
  {
    index: 19,
    name: "power-of-attorney",
    next: () => "user-info",
    isValid: ({ animal, ...state }) =>
      Boolean(
        selectCurrentInsuranceByAnimalId(state, animal?.id).insurely
          ?.customerSignature
      ),
  },
  {
    index: 20,
    name: "draft-summary",
    next: () => "payment",
    isValid: () => true,
  },
  {
    index: 20,
    name: "draft-summary-flytthjalp",
    next: () => "payment",
    isValid: () => true,
  },
  {
    index: 20,
    name: "payment",
    next: () => "thank-you",
    isValid: () => true,
  },
  {
    index: 21,
    name: "thank-you",
    isValid: () => true,
    next: () => "thank-you",
  },
];
